import { useEffect, useMemo, useState } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from "@/integrations/supabase/client";
import useUser from '@/hooks/useUser';

// Configuración por defecto
const DEFAULT_CONFIG = {
  points_per_action: {
    correct_answer: 20,
    speed_bonus: {
      fast: 5,
      medium: 3,
      slow: 1
    },
    streak_bonus: {
      "3_streak": 5,
      "5_streak": 10,
      "10_streak": 25
    },
    perfect_round: {
      "5_questions": 20,
      "10_questions": 50
    },
    difficulty_multiplier: {
      easy: 1,
      medium: 1.5,
      hard: 2
    }
  },
  min_points_for_cerebrix: 100,
  max_daily_cerebrix_free: 20,
  max_daily_cerebrix_premium: 100
} as const;

// Estado inicial del juego
const INITIAL_STATE = {
  score: 0,
  timeLeft: 60,
  isGameOver: false,
  currentProblem: null,
  totalProblems: 0,
  correctAnswers: 0,
  streak: 0,
  maxStreak: 0,
  cerebrixEarned: 0,
  gameStarted: false,
  gameConfig: DEFAULT_CONFIG
} as const;

// Tipos para la configuración del juego
type GameConfig = {
  points_per_action: {
    correct_answer: number;
    speed_bonus: {
      fast: number;
      medium: number;
      slow: number;
    };
    streak_bonus: {
      "3_streak": number;
      "5_streak": number;
      "10_streak": number;
    };
    perfect_round: {
      "5_questions": number;
      "10_questions": number;
    };
    difficulty_multiplier: {
      easy: number;
      medium: number;
      hard: number;
    };
  };
  min_points_for_cerebrix: number;
  max_daily_cerebrix_free: number;
  max_daily_cerebrix_premium: number;
};

type GameState = typeof INITIAL_STATE;

// Tipos para el problema actual
type Problem = {
  expression: string;
  answer: number;
  options: number[];
};

export const useCalculatorGame = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user, profile } = useUser();
  const gameId = useMemo(() => '6e80c558-2caf-4916-9b56-b51b841c7f53', []);

  const [gameState, setGameState] = useState<typeof INITIAL_STATE>({ ...INITIAL_STATE });

  // Cargar configuración del juego
  useEffect(() => {
    const loadGameConfig = async () => {
      try {
        const { data: configs, error: listError } = await supabase
          .from('game_config')
          .select('*')
          .eq('game_id', gameId)
          .order('created_at', { ascending: false })
          .limit(1);

        if (listError) {
          console.error('Error cargando configuración:', listError);
          return;
        }

        // Si no hay configuración, mantener la por defecto
        if (!configs || configs.length === 0) {
          console.log('No se encontró configuración, usando valores por defecto');
          return;
        }

        const config = configs[0];
        console.log('Configuración cargada:', config);

        // Asegurar que la configuración tenga todos los valores necesarios
        const validConfig = {
          points_per_action: {
            correct_answer: config.points_per_action?.correct_answer || DEFAULT_CONFIG.points_per_action.correct_answer,
            speed_bonus: {
              fast: config.points_per_action?.speed_bonus?.fast || DEFAULT_CONFIG.points_per_action.speed_bonus.fast,
              medium: config.points_per_action?.speed_bonus?.medium || DEFAULT_CONFIG.points_per_action.speed_bonus.medium,
              slow: config.points_per_action?.speed_bonus?.slow || DEFAULT_CONFIG.points_per_action.speed_bonus.slow
            },
            streak_bonus: {
              "3_streak": config.points_per_action?.streak_bonus?.["3_streak"] || DEFAULT_CONFIG.points_per_action.streak_bonus["3_streak"],
              "5_streak": config.points_per_action?.streak_bonus?.["5_streak"] || DEFAULT_CONFIG.points_per_action.streak_bonus["5_streak"],
              "10_streak": config.points_per_action?.streak_bonus?.["10_streak"] || DEFAULT_CONFIG.points_per_action.streak_bonus["10_streak"]
            },
            perfect_round: {
              "5_questions": config.points_per_action?.perfect_round?.["5_questions"] || DEFAULT_CONFIG.points_per_action.perfect_round["5_questions"],
              "10_questions": config.points_per_action?.perfect_round?.["10_questions"] || DEFAULT_CONFIG.points_per_action.perfect_round["10_questions"]
            },
            difficulty_multiplier: {
              easy: config.points_per_action?.difficulty_multiplier?.easy || DEFAULT_CONFIG.points_per_action.difficulty_multiplier.easy,
              medium: config.points_per_action?.difficulty_multiplier?.medium || DEFAULT_CONFIG.points_per_action.difficulty_multiplier.medium,
              hard: config.points_per_action?.difficulty_multiplier?.hard || DEFAULT_CONFIG.points_per_action.difficulty_multiplier.hard
            }
          },
          min_points_for_cerebrix: config.min_points_for_cerebrix || DEFAULT_CONFIG.min_points_for_cerebrix,
          max_daily_cerebrix_free: config.max_daily_cerebrix_free || DEFAULT_CONFIG.max_daily_cerebrix_free,
          max_daily_cerebrix_premium: config.max_daily_cerebrix_premium || DEFAULT_CONFIG.max_daily_cerebrix_premium
        };

        setGameState(prev => ({
          ...prev,
          gameConfig: validConfig
        }));
      } catch (error) {
        console.error('Error loading game config:', error);
      }
    };

    loadGameConfig();
  }, [gameId]);

  // Calcular Cerebrix ganados
  const calculateCerebrix = async (score: number) => {
    if (!gameState.gameConfig) return 0;
    if (score < gameState.gameConfig.min_points_for_cerebrix) return 0;

    // Obtener el total de Cerebrix ganados hoy
    const today = new Date().toISOString().split('T')[0];
    const { data: dailyProgress } = await supabase
      .from('daily_game_progress')
      .select('cerebrix_earned')
      .eq('user_id', user?.id)
      .eq('date', today)
      .single();

    const dailyCerebrix = dailyProgress?.cerebrix_earned || 0;
    const maxDailyCerebrix = profile?.subscription_status === 'active' 
      ? gameState.gameConfig.max_daily_cerebrix_premium 
      : gameState.gameConfig.max_daily_cerebrix_free;

    // Si ya alcanzó el límite diario
    if (dailyCerebrix >= maxDailyCerebrix) return 0;

    // Calcular nuevos Cerebrix
    const newCerebrix = Math.floor((score - gameState.gameConfig.min_points_for_cerebrix) / 100);
    
    // Limitar al máximo diario
    return Math.min(newCerebrix, maxDailyCerebrix - dailyCerebrix);
  };

  // Actualizar el puntaje
  const updateScore = (points: number) => {
    setGameState(prev => {
      const newScore = prev.score + points;
      console.log('Score actualizado:', newScore);
      return {
        ...prev,
        score: newScore
      };
    });
  };

  // Terminar el juego
  const endGame = async (finalScore: number, finalCorrectAnswers: number, finalMaxStreak: number) => {
    console.log('Estado final del juego:', {
      score: finalScore,
      correctAnswers: finalCorrectAnswers,
      maxStreak: finalMaxStreak,
      timeLeft: gameState.timeLeft,
      isGameOver: gameState.isGameOver
    });

    // Primero actualizamos el estado para marcar el juego como terminado
    setGameState(prev => ({
      ...prev,
      isGameOver: true,
      timeLeft: 0,
      score: finalScore,
      correctAnswers: finalCorrectAnswers,
      maxStreak: finalMaxStreak
    }));

    // Luego guardamos el progreso si hay puntos
    if (finalScore > 0) {
      await saveProgress(finalScore, finalCorrectAnswers, finalMaxStreak);
    }
  };

  // Guardar progreso del juego
  const saveProgress = async (finalScore: number, correctAnswers: number, maxStreak: number) => {
    if (!user) return;

    console.log('Estado actual al guardar:', {
      score: finalScore,
      correctAnswers,
      maxStreak
    });

    try {
      // Registrar la sesión de juego
      const { data: sessionResult, error: sessionError } = await supabase
        .rpc('register_game_session', {
          p_user_id: user.id,
          p_game_id: gameId,
          p_points_earned: finalScore,
          p_session_duration: 60,
          p_difficulty_level: 'basic',
          p_bonus_multiplier: 1.0
        });

      if (sessionError) {
        console.error('Error guardando sesión:', sessionError);
        toast({
          title: 'Error',
          description: 'No se pudo guardar tu progreso',
          variant: 'destructive'
        });
        return;
      }

      console.log('Sesión guardada:', sessionResult);
      
      // Actualizar la caché del cliente
      queryClient.invalidateQueries(['gameProgress']);
      queryClient.invalidateQueries(['profile']); // Invalidar el perfil para actualizar los Cerebrix
      queryClient.invalidateQueries(['cerebrixStats']); // Invalidar estadísticas de Cerebrix
      
      const cerebrixGanados = sessionResult?.cerebrix_converted || 0;
      toast({
        title: '¡Progreso guardado!',
        description: `Obtuviste ${finalScore} puntos${cerebrixGanados > 0 ? ` y ${cerebrixGanados} Cerebrix` : ''}`
      });

    } catch (error) {
      console.error('Error guardando progreso:', error);
      toast({
        title: 'Error',
        description: 'No se pudo guardar tu progreso',
        variant: 'destructive'
      });
    }
  };

  // Generar un nuevo problema
  const generateProblem = () => {
    const operations = ['+', '-', '*'];
    const operation = operations[Math.floor(Math.random() * operations.length)];
    let num1, num2;

    switch (operation) {
      case '+':
        num1 = Math.floor(Math.random() * 50) + 1;
        num2 = Math.floor(Math.random() * 50) + 1;
        break;
      case '-':
        num1 = Math.floor(Math.random() * 50) + 25;
        num2 = Math.floor(Math.random() * num1);
        break;
      case '*':
        num1 = Math.floor(Math.random() * 12) + 1;
        num2 = Math.floor(Math.random() * 12) + 1;
        break;
      default:
        num1 = Math.floor(Math.random() * 50) + 1;
        num2 = Math.floor(Math.random() * 50) + 1;
    }

    const expression = `${num1} ${operation} ${num2}`;
    const answer = eval(expression);

    // Generar opciones incorrectas
    const options = [answer];
    while (options.length < 4) {
      const offset = Math.floor(Math.random() * 10) + 1;
      const wrongAnswer = answer + (Math.random() < 0.5 ? offset : -offset);
      if (!options.includes(wrongAnswer)) {
        options.push(wrongAnswer);
      }
    }

    // Mezclar opciones
    for (let i = options.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [options[i], options[j]] = [options[j], options[i]];
    }

    return {
      expression,
      answer,
      options
    };
  };

  // Verificar respuesta
  const checkAnswer = async (selectedAnswer: number) => {
    if (!gameState.currentProblem || gameState.isGameOver) return;

    const isCorrect = selectedAnswer === gameState.currentProblem.answer;
    
    // Obtener la configuración de puntos
    const config = gameState.gameConfig?.points_per_action || DEFAULT_CONFIG.points_per_action;
    
    // Asegurar que todos los valores estén definidos
    const currentStreak = gameState.streak || 0;
    const currentScore = Number(gameState.score || 0);
    const correctAnswers = gameState.correctAnswers || 0;
    
    // Calcular puntos base por respuesta correcta
    const basePoints = isCorrect ? (config.correct_answer || 20) : 0;
    
    // Calcular bonus por racha
    let streakBonus = 0;
    if (isCorrect) {
      if (currentStreak >= 10) {
        streakBonus = config.streak_bonus?.["10_streak"] || 25;
      } else if (currentStreak >= 5) {
        streakBonus = config.streak_bonus?.["5_streak"] || 10;
      } else if (currentStreak >= 3) {
        streakBonus = config.streak_bonus?.["3_streak"] || 5;
      }
    }

    const totalPoints = basePoints + streakBonus;
    const newStreak = isCorrect ? currentStreak + 1 : 0;

    console.log('Estado actual:', {
      currentScore,
      currentStreak,
      basePoints,
      streakBonus,
      totalPoints,
      newStreak
    });

    // Actualizar estado inmediatamente
    setGameState(prev => {
      const updatedScore = currentScore + totalPoints;
      console.log('Score actualizado:', updatedScore);
      
      return {
        ...prev,
        score: updatedScore,
        correctAnswers: isCorrect ? correctAnswers + 1 : correctAnswers,
        streak: newStreak,
        maxStreak: Math.max(prev.maxStreak || 0, newStreak),
        currentProblem: generateProblem()
      };
    });
  };

  // Iniciar el juego
  const startGame = () => {
    const initialGameState = {
      ...INITIAL_STATE,
      gameStarted: true,
      currentProblem: generateProblem(),
      score: 0,
      streak: 0,
      maxStreak: 0,
      correctAnswers: 0,
      cerebrixEarned: 0,
      gameConfig: {
        ...DEFAULT_CONFIG,
        points_per_action: {
          correct_answer: 20,
          speed_bonus: {
            fast: 5,
            medium: 3,
            slow: 1
          },
          streak_bonus: {
            "3_streak": 5,
            "5_streak": 10,
            "10_streak": 25
          },
          perfect_round: {
            "5_questions": 20,
            "10_questions": 50
          },
          difficulty_multiplier: {
            easy: 1,
            medium: 1.5,
            hard: 2
          }
        }
      }
    };

    setGameState(initialGameState);
  };

  // Reiniciar el juego
  const resetGame = () => {
    setGameState({
      ...INITIAL_STATE,
      gameConfig: gameState.gameConfig // Mantener la configuración actual
    });
    startGame();
  };

  // Manejar el temporizador
  useEffect(() => {
    if (gameState.gameStarted && !gameState.isGameOver) {
      const timer = setInterval(() => {
        setGameState(prev => {
          if (prev.timeLeft <= 1) {
            clearInterval(timer);
            const finalState = {
              ...prev,
              timeLeft: 0,
              isGameOver: true
            };
            // Asegurarnos de que endGame use los valores correctos
            endGame(prev.score, prev.correctAnswers, prev.maxStreak);
            return finalState;
          }
          return { ...prev, timeLeft: prev.timeLeft - 1 };
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [gameState.gameStarted, gameState.isGameOver]);

  return {
    gameState,
    startGame,
    checkAnswer,
    endGame,
    resetGame,
    saveProgress
  };
};
